/**
 * Section words in every language the catalog ships, ported from jnana-native's
 * `wordsByLanguageCodes` (jnana-native/src/types/languages.ts).
 *
 * Why this exists: the web fetches lyrics in the reader's own language
 * (`BhajanDetails` passes `language` to `fetchLyrics`), but the heading regex used
 * to match Latin/IAST spellings only. In the other nine scripts nothing matched, so
 * the whole document collapsed into one 'other' stanza — headings rendered as
 * ordinary lyric lines and Repeat Pallavi silently did nothing at all.
 *
 * Keep this table in step with native's.
 */
export interface SectionWords {
  pallavi: string;
  anupallavi: string;
  caranam: string;
  slokam: string;
  /** Stotram structure words — see stotramParser, which reads these four. */
  dhyanam: string;
  sampurnam: string;
  uvaca: string;
  /**
   * Not in native's wordsByLanguageCodes; these spellings come from lyrics 8238,
   * which carries the heading in all ten languages. Without them the phala
   * śruti heading was matched only in Latin and Devanagari.
   */
  phalasruti: string;
}

const SECTION_WORDS_BY_LANGUAGE: Record<string, SectionWords> = {
  en: { pallavi: 'pallavi', anupallavi: 'anupallavi', caranam: 'caraṇaṁ', slokam: 'ślōkaṁ', dhyanam: 'dhyānam', sampurnam: 'sampūrṇam', uvaca: 'uvāca', phalasruti: 'phalaśruti' },
  te: { pallavi: 'పల్లవి', anupallavi: 'అనుపల్లవి', caranam: 'చరణం', slokam: 'శ్లోకం', dhyanam: 'ధ్యానం', sampurnam: 'సంపూర్ణం', uvaca: 'ఉవాచ', phalasruti: 'ఫలశ్రుతి' },
  kn: { pallavi: 'ಪಲ್ಲವಿ', anupallavi: 'ಅನುಪಲ್ಲವಿ', caranam: 'ಚರಣಂ', slokam: 'ಶ್ಲೋಕಂ', dhyanam: 'ಧ್ಯಾನಂ', sampurnam: 'ಸಂಪೂರ್ಣಂ', uvaca: 'ಉವಾಚ', phalasruti: 'ಫಲಶ್ರುತಿ' },
  hi: { pallavi: 'पल्लवि', anupallavi: 'अनुपल्लवि', caranam: 'चरणं', slokam: 'श्लोकं', dhyanam: 'ध्यानं', sampurnam: 'संपूर्णं', uvaca: 'उवाच', phalasruti: 'फलश्रुति' },
  ta: { pallavi: 'பல்லவி', anupallavi: 'அனுபல்லவி', caranam: 'சரணம்', slokam: 'ஶ்லோகம்', dhyanam: 'த்யானம்', sampurnam: 'ஸம்பூர்ணம்', uvaca: 'உவாச', phalasruti: 'பலஶ்ருதி' },
  gu: { pallavi: 'પલ્લવિ', anupallavi: 'અનુપલ્લવિ', caranam: 'ચરણં', slokam: 'શ્લોકં', dhyanam: 'ધ્યાનં', sampurnam: 'સંપૂર્ણં', uvaca: 'ઉવાચ', phalasruti: 'ફલશ્રુતિ' },
  bn: { pallavi: 'পল্লবি', anupallavi: 'অনুপল্লবি', caranam: 'চরণং', slokam: 'শ্লোকং', dhyanam: 'ধ্যানং', sampurnam: 'সংপূর্ণং', uvaca: 'উবাচ', phalasruti: 'ফলশ্রুতি' },
  ml: { pallavi: 'പല്ലവി', anupallavi: 'അനുപല്ലവി', caranam: 'ചരണം', slokam: 'ശ്ലോകം', dhyanam: 'ധ്യാനം', sampurnam: 'സംപൂർണം', uvaca: 'ഉവാച', phalasruti: 'ഫലശ്രുതി' },
  or: { pallavi: 'ପଲ୍ଲଵି', anupallavi: 'ଅନୁପଲ୍ଲଵି', caranam: 'ଚରଣଂ', slokam: 'ଶ୍ଲୋକଂ', dhyanam: 'ଧ୍ୟାନଂ', sampurnam: 'ସଂପୂର୍ଣଂ', uvaca: 'ଉବାଚ', phalasruti: 'ଫଲଶ୍ରୁତି' },
  pa: { pallavi: 'ਪਲ੍ਲਵਿ', anupallavi: 'ਅਨੁਪਲ੍ਲਵਿ', caranam: 'ਚਰਣੰ', slokam: 'ਸ਼੍ਲੋਕੰ', dhyanam: 'ਧ੍ਯਾਨੰ', sampurnam: 'ਸਂਪੂਰ੍ਣੰ', uvaca: 'ਉਵਾਚ', phalasruti: 'ਫਲਸ਼੍ਰੁਤਿ' },
};

export function sectionWordsForLanguage(languageCode: string): SectionWords {
  return SECTION_WORDS_BY_LANGUAGE[languageCode] ?? SECTION_WORDS_BY_LANGUAGE.en;
}

/**
 * The nine Indic blocks the catalog uses, by their first code point. Each is
 * laid out identically: anusvara at +0x02, ma at +0x2E, virama at +0x4D.
 * Devanagari, Bengali, Gurmukhi, Gujarati, Oriya, Tamil, Telugu, Kannada,
 * Malayalam.
 */
const INDIC_BLOCK_BASES = [0x0900, 0x0980, 0x0a00, 0x0a80, 0x0b00, 0x0b80, 0x0c00, 0x0c80, 0x0d00];

const ANUSVARA = 0x02;
const LETTER_MA = 0x2e;
const VIRAMA = 0x4d;

/** Gurmukhi writes its nasal with either TIPPI or BINDI; the table uses TIPPI. */
const GURMUKHI_TIPPI = 0x0a70;
const GURMUKHI_BINDI = 0x0a02;

/**
 * Single letters the catalog and the word table spell differently. Both pairs
 * are Oriya, and both were found by comparing lyrics 104555 against the table:
 * YA (U+0B2F) / YYA (U+0B5F) in dhyānam, and VA (U+0B35) / BA (U+0B2C) in
 * uvāca. Oriya was the last script still missing its headings after the
 * final-nasal forms were handled.
 */
const EQUIVALENT_LETTERS: ReadonlyArray<readonly [string, string]> = [
  ['ଯ', 'ୟ'],
  ['ଵ', 'ବ'],
];

function blockBaseOf(codePoint: number): number | undefined {
  return INDIC_BLOCK_BASES.find((base) => codePoint >= base && codePoint < base + 0x80);
}

/** Every way this word's final nasal can be written, given how it is written now. */
function nasalForms(word: string): string[] {
  const chars = Array.from(word);
  const last = chars[chars.length - 1]?.codePointAt(0);
  if (last == null) return [word];

  const base = blockBaseOf(last);
  if (base == null) return [word];

  const isAnusvara =
    last === base + ANUSVARA || last === GURMUKHI_TIPPI || last === GURMUKHI_BINDI;

  // …ం  ->  …మ్  (and, in Gurmukhi, tippi <-> bindi)
  if (isAnusvara) {
    const stem = chars.slice(0, -1).join('');
    const forms = [word, stem + String.fromCodePoint(base + LETTER_MA, base + VIRAMA)];
    if (base === 0x0a00) {
      forms.push(stem + String.fromCodePoint(GURMUKHI_TIPPI));
      forms.push(stem + String.fromCodePoint(GURMUKHI_BINDI));
    }
    return forms;
  }

  // …మ్  ->  …ం
  const prev = chars[chars.length - 2]?.codePointAt(0);
  if (last === base + VIRAMA && prev === base + LETTER_MA) {
    const stem = chars.slice(0, -2).join('');
    const forms = [word, stem + String.fromCodePoint(base + ANUSVARA)];
    if (base === 0x0a00) forms.push(stem + String.fromCodePoint(GURMUKHI_TIPPI));
    return forms;
  }

  return [word];
}

const INDEPENDENT_U = 0x09;
const VOWEL_SIGN_U = 0x41;

/**
 * The form a vowel-initial word takes when sandhi fuses it to the previous word.
 *
 * "uvāca" begins with an independent vowel (ఉ, U+0C09). Joined to a speaker's
 * name it becomes a vowel SIGN on the preceding consonant — "దేవి" + "ఉవాచ"
 * is written "దేవ్యువాచ", carrying ు (U+0C41) rather than ఉ. The catalog uses
 * the fused spelling for ~50 headings, most of them the "śrī bhagavān uvāca"
 * markers in the Bhagavad Gīta chapters.
 *
 * Only the independent-U case is handled, because uvāca is the only heading word
 * that starts with a vowel. The Indic blocks put independent U at +0x09 and its
 * matra at +0x41, verified against the Telugu and Kannada spellings in the data.
 */
export function sandhiInitialVariants(word: string): string[] {
  const first = word.codePointAt(0);
  if (first == null) return [word];
  const base = blockBaseOf(first);
  if (base == null || first !== base + INDEPENDENT_U) return [word];
  const rest = Array.from(word).slice(1).join('');
  return [word, String.fromCodePoint(base + VOWEL_SIGN_U) + rest];
}

/**
 * Every spelling of a heading word that the catalog might actually use.
 *
 * The same word is written more than one way, and which way is a property of
 * the individual text rather than of the language. A word-final "ṁ" is either
 * an anusvara (చరణం) or ma + virama (ధ్యానమ్) — lyrics 101215 writes its
 * caraṇaṁ heading the first way in Telugu while lyrics 104555 writes its
 * dhyānam heading the second way, in the same language. Gurmukhi has two nasal
 * signs, TIPPI and BINDI, and Oriya spells the same sound with either YA or
 * YYA.
 *
 * Matching only the form the word table happens to carry drops the heading in
 * eight of the nine non-English scripts. Verified against lyrics 104555 in all
 * ten languages on 2026-07-26; every pair comes from that comparison, not from
 * theory — Tamil, whose two nasal forms coincide, is the one language that
 * needed nothing.
 */
export function finalNasalVariants(word: string): string[] {
  // Swap equivalent letters first, then vary the ending of each spelling.
  const stems = new Set<string>([word]);
  for (const [a, b] of EQUIVALENT_LETTERS) {
    for (const stem of Array.from(stems)) {
      if (stem.includes(a)) stems.add(stem.split(a).join(b));
      if (stem.includes(b)) stems.add(stem.split(b).join(a));
    }
  }
  const out = new Set<string>();
  // Array.from rather than iterating the Set directly: this project's tsconfig
  // targets es5, where for..of over a Set needs --downlevelIteration.
  for (const stem of Array.from(stems)) {
    for (const form of nasalForms(stem)) out.add(form);
  }
  return Array.from(out);
}

/** Escape a word for literal use inside a regex. */
function escape(word: string): string {
  return word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The Latin/IAST spellings the catalog uses regardless of the reader's language.
 * Transliterated headings appear in real data, so these are ALWAYS in the
 * alternation alongside the language's own script — dropping them would regress
 * every English bhajan.
 *
 * These are regex fragments, not literals, so they are never passed through
 * `escape`.
 */
const ALWAYS_LATIN = [
  'pallavi',
  'anupallavi',
  'caraṇa[mṁ]?',
  'charanam',
  'caranam',
  'samasti\\s+caraṇa[mṁ]?',
  'chittasvara',
  'kalpanasvara',
  'śloka',
  'sloka',
  'maṅgaḷam',
  'mangalam',
  'svaram',
  'svara',
];

/**
 * Longest-first, always. `pallavi` is a substring of `anupallavi` in every script,
 * so a shortest-first alternation would classify every anupallavi as a pallavi.
 */
function longestFirst(parts: string[]): string {
  return Array.from(new Set(parts))
    .sort((a, b) => b.length - a.length)
    .join('|');
}

function alternation(languageCode: string): string {
  const words = sectionWordsForLanguage(languageCode);
  const localised = [words.pallavi, words.anupallavi, words.caranam, words.slokam].map(escape);
  return longestFirst([...localised, ...ALWAYS_LATIN]);
}

/**
 * Matches a whole heading line — anchored at both ends, so a lyric that merely
 * contains the word "pallavi" is never mistaken for a section heading.
 */
export function buildSectionRegex(languageCode: string): RegExp {
  return new RegExp(`^\\s*(${alternation(languageCode)})\\s*[:：]?\\s*$`, 'i');
}

function buildOneWordRegex(word: string, extraLatin: string[]): RegExp {
  return new RegExp(`^\\s*(${longestFirst([escape(word), ...extraLatin])})\\s*[:：]?\\s*$`, 'i');
}

export function buildPallaviRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).pallavi, ['pallavi']);
}

export function buildAnupallaviRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).anupallavi, ['anupallavi']);
}

export function buildCaranamRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).caranam, [
    'caraṇa[mṁ]?',
    'charanam',
    'caranam',
    'samasti\\s+caraṇa[mṁ]?',
  ]);
}
